import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * avameo Brand-Komponenten nach §4/§6/§9 des Styleguides.
 *
 * - `Wordmark` — mehrfarbiger "avameo"-Schriftzug, italic 2.4°
 * - `FaktLogo` — Produkt-Wordmark "FAKT" mit den vier Eck-Farben des
 *               App-Icons. Zeigt sich als Schriftzug in der App-UI
 *               (Sidebar/Mobile-Top-Bar), korrespondiert visuell mit
 *               dem PWA-Icon (icon-source.svg).
 * - `RingConnector` — Quarter-Arc (Signature-Motiv)
 * - `RingMotif` — offener Kreis (dekorativ)
 * - `SectionTitle` — blaue Überschrift mit partieller Unterstreichung
 */

/** 6-Letter-Colors aus §6 */
const LETTER_COLORS = ['#009DE0', '#009AB9', '#009891', '#159569', '#69A840', '#A1BF35'] as const;
const LETTERS = ['a', 'v', 'a', 'm', 'e', 'o'] as const;

/**
 * FAKT-Gradient — der durchlaufende Cyan→Lime-Verlauf aus dem Master-
 * Asset (icon-source.svg, Brand-Pack 28.04.2026). Die 6 Stops sind die
 * sechs avameo-Letter-Colors gleichmaessig verteilt.
 */
const FAKT_GRADIENT =
  'linear-gradient(90deg, #009DE0 0%, #009AB9 20%, #009891 40%, #159569 60%, #69A840 80%, #A1BF35 100%)';
/** Hintergrund-Farbe des Master-Icons (Brand-Pack); fuer Splash-Screen,
 *  PWA theme_color und Co. */
export const FAKT_DARK = '#0B1B2E';

export interface WordmarkProps {
  size?: number;
  mono?: string; // bei gesetzt: alle Buchstaben in dieser Farbe
  tagline?: boolean;
  taglineColor?: string;
  className?: string;
}

export function Wordmark({
  size = 28,
  mono,
  tagline = false,
  taglineColor,
  className,
}: WordmarkProps) {
  const colors = mono ? LETTERS.map(() => mono) : LETTER_COLORS;
  return (
    <div className={cn('inline-flex flex-col', className)} style={{ lineHeight: 0.95 }}>
      <div
        style={{
          display: 'inline-flex',
          fontFamily: "'Calibri', 'Carlito', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontSize: size,
          fontWeight: 700,
          transform: 'skewX(-2.4deg)',
          letterSpacing: '-0.015em',
        }}
      >
        {LETTERS.map((l, i) => (
          <span key={i} style={{ color: colors[i] }}>
            {l}
          </span>
        ))}
      </div>
      {tagline && (
        <div
          style={{
            fontSize: Math.max(9, size * 0.28),
            fontFamily: "'Calibri', 'Carlito', 'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 400,
            color: taglineColor ?? '#606060',
            letterSpacing: '0.04em',
            marginTop: Math.max(2, size * 0.1),
          }}
        >
          the digital experts
        </div>
      )}
    </div>
  );
}

export interface FaktLogoProps {
  /** Schrift-Pixelgroesse. Default 22 (passt zur Sidebar/Top-Bar). */
  size?: number;
  /** Bei gesetzt: einfarbig in dieser Farbe statt Gradient (z.B. fuer Mono-Kontexte). */
  mono?: string;
  /** Akzentstriche unter dem Schriftzug (Bilanzlinie-Motiv aus dem App-Icon).
   *  Default false — fuer kompakte Inline-Verwendung. Auf groesseren
   *  Brand-Flaechen (Login-Card, Splash) auf true setzen. */
  bars?: boolean;
  className?: string;
}

/**
 * Produkt-Wordmark "FAKT" (Brand-Pack 28.04.2026, Variante 03 · Bilanzlinie).
 * Inter Tight 900, aufrecht, mit Cyan→Lime-Gradient-Fuellung — passt
 * exakt zum App-Icon (icon-source.svg). Inter Tight wird in index.html
 * via Google Fonts geladen.
 */
export function FaktLogo({ size = 22, mono, bars = false, className }: FaktLogoProps) {
  const fillStyle: CSSProperties = mono
    ? { color: mono }
    : {
        background: FAKT_GRADIENT,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
      };
  // Strich-Hoehen aus dem Master-SVG (16/36/66 bei viewBox 1024) auf
  // Schrift-Pixelhoehe normiert. fontSize 22 → ~3/6/10 px.
  const barUnit = size / 22;
  return (
    <div className={cn('inline-flex flex-col items-start', className)} style={{ lineHeight: 1 }}>
      <span
        style={{
          fontFamily: "'Inter Tight', 'Helvetica Neue', 'Arial Black', sans-serif",
          fontSize: size,
          fontWeight: 900,
          letterSpacing: '-0.05em',
          ...fillStyle,
        }}
      >
        FAKT
      </span>
      {bars && (
        <div
          className="mt-[0.18em] flex items-end gap-[0.16em]"
          style={{ width: '100%' }}
          aria-hidden
        >
          <span style={{ flex: 1, height: barUnit * 2, background: mono ?? '#009DE0' }} />
          <span style={{ flex: 1, height: barUnit * 4, background: mono ?? '#159569' }} />
          <span style={{ flex: 1, height: barUnit * 7, background: mono ?? '#A1BF35' }} />
        </div>
      )}
    </div>
  );
}

/**
 * Quarter-Arc als Signature-Motiv. Kann durch `rotation` in jede
 * der vier Ecken gedreht werden (0, 90, 180, 270).
 */
export function RingConnector({
  size = 64,
  color = '#E6E6E6',
  rotation = 0,
  thickness = 0.38,
  className,
  style,
}: {
  size?: number;
  color?: string;
  rotation?: number;
  thickness?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const inner = 100 * (1 - thickness);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={{ transform: `rotate(${rotation}deg)`, display: 'block', ...style }}
    >
      <path
        d={`M 100 0 A 100 100 0 0 0 0 100 L 0 ${100 - inner} A ${inner} ${inner} 0 0 1 ${inner} 0 Z`}
        fill={color}
      />
    </svg>
  );
}

/** Offener Kreis — für dekorative Akzente */
export function RingMotif({
  size = 120,
  color = '#E6E6E6',
  stroke = 14,
  className,
  style,
}: {
  size?: number;
  color?: string;
  stroke?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={{ display: 'block', ...style }}
    >
      <circle cx="50" cy="50" r={50 - stroke / 2} fill="none" stroke={color} strokeWidth={stroke} />
    </svg>
  );
}

/**
 * Blaue Section-Überschrift mit partiell unterstrichenem Titel.
 * Der `underline`-Wert ist ein Anteil (0.0 bis 1.0) und bestimmt wie
 * viel des Titels die Unterstreichung abdeckt — das ist ein
 * charakteristisches §9-Styleguide-Element.
 */
export function SectionTitle({
  children,
  underline = 0.38,
  size = 20,
  color,
  className,
}: {
  children: ReactNode;
  underline?: number;
  size?: number;
  color?: string;
  className?: string;
}) {
  return (
    <div className={cn('relative inline-block', className)}>
      <div
        className="font-sans font-normal"
        style={{
          fontSize: size,
          color: color ?? '#00A6E5',
          letterSpacing: '-0.005em',
          lineHeight: 1.15,
          paddingBottom: 6,
        }}
      >
        {children}
      </div>
      <div
        className="absolute bottom-0 left-0 h-[2px]"
        style={{
          width: `${underline * 100}%`,
          background: color ?? '#00A6E5',
        }}
      />
    </div>
  );
}
